import React from "react"
import { Form } from "@carrotfertility/carotene-core-x"
import { AboutYou } from "#/types/carrotPlanTypes"
import { ContinueButton } from "#/components/carrot-plans/shared/ButtonFooter"
import { FormattedMessage, useIntl } from "react-intl"
import {
  Address,
  AddressFields,
  InternationalAddress,
  InternationalAddressFields,
  formatAddressFieldsToPayload,
  formatInternationalAddressFieldsToPayload
} from "#/services/common-forms"
import { Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { useTrackView } from "#/utils/heap"
import { RegistrationFlowTitle } from "#/components/views/register/RegistrationFlowTitle"
import { RegistrationFlowLayout } from "#/components/views/register/RegistrationLayouts"
import { RegistrationButtonFooter } from "#/components/views/register/RegistrationButtons"

type FormValues = AddressFields | InternationalAddressFields

export default function AddressStep({
  isUsa,
  address,
  onBack,
  onComplete,
  employeeId
}: {
  isUsa: boolean
  address: AboutYou["address"] | null
  onBack: () => void
  onComplete: (address: AboutYou["address"]) => void
  employeeId: number
}): JSX.Element {
  useTrackView("AddressRegFlow", "Registration address screen")
  const theme = useTheme()
  const intl = useIntl()

  function onSubmit(data: FormValues): void {
    const aboutYouUpdate = isUsa
      ? formatAddressFieldsToPayload(data as AddressFields)
      : formatInternationalAddressFieldsToPayload(data as InternationalAddressFields)
    onComplete(aboutYouUpdate.address as AboutYou["address"])
  }

  const header = intl.formatMessage({
    defaultMessage: "What's your home address?"
  })

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        employeeId={employeeId}
        header={header}
        description={
          <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
            <Typography>
              <FormattedMessage defaultMessage="Your address helps us recommend the right care and providers near you." />
            </Typography>
            <Typography variant="body2">
              <FormattedMessage defaultMessage="Fields marked with an asterisk are required" />
            </Typography>
          </Stack>
        }
        interactiveElements={
          <Form onSubmit={onSubmit}>
            {isUsa ? <Address defaultValues={address} /> : <InternationalAddress defaultValues={address} />}
            <RegistrationButtonFooter
              onBack={onBack}
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Form>
        }
      />
    </>
  )
}
